// @flow

import { partial } from 'lodash';

import { RouterSelectors } from '@zapier/toolbox-redux-framework';
import { isSubUrlOf } from '@zapier/url-utils';

import { createKeyFromService } from 'app/developer-v3/utils';
import {
  BASE_APP_URL,
  LEGACY_BUILDER_BASE_URL,
} from 'app/developer-v3/constants';

import type { ActionType, ServiceType } from 'app/developer-v3/types';

/**
 * Builds the app URL based on ID and legacy check
 *
 * @param {number|string} appId
 * @param {boolean} isLegacy
 */
const getAppUrl = (appId: number | string, isLegacy: boolean = false): string =>
  `${isLegacy ? LEGACY_BUILDER_BASE_URL : BASE_APP_URL}/app/${appId}`;

/**
 * Given the app ID and the version number provided
 * returns the base app url
 *
 * @param {number|string} appId
 * @param {string} appVersionNumber
 */
const getAppVersionUrl = (
  appId: number | string,
  appVersionNumber: string
): string => `${BASE_APP_URL}/app/${appId}/version/${appVersionNumber}`;

const getBaseAuthUrl = (
  appId: number | string,
  appVersionNumber: string
): string => `${getAppVersionUrl(appId, appVersionNumber)}/authentication`;

const getAuthUrlForFieldKey = (
  appId: number | string,
  appVersionNumber: string,
  fieldKey: string
): string => `${getBaseAuthUrl(appId, appVersionNumber)}/fields/${fieldKey}`;

const getAdvancedUrl = (appId: number | string, appVersionNumber: string) =>
  `${getAppVersionUrl(appId, appVersionNumber)}/advanced`;

const getServiceUrl = (
  type: ServiceType,
  serviceOptions: {
    id: string,
    version: string,
    [serviceKey: string]: string,
    actionType?: ActionType,
  }
) => {
  const { id: appId, version, actionType, actionKey } = serviceOptions;
  const serviceKey = serviceOptions[createKeyFromService(type)];
  const baseUrl = `${getAppVersionUrl(appId, version)}`;

  return actionType
    ? `${baseUrl}/actions/${actionType}/${actionKey}`
    : `${baseUrl}/${type}/${serviceKey}`;
};

const getActionUrl = partial(getServiceUrl, 'actions');
const getTriggerUrl = partial(getServiceUrl, 'triggers');

const isVBOrLegacyUrl = () => {
  if (typeof window === undefined) {
    return false;
  }

  return (
    isSubUrlOf(window.location.pathname, BASE_APP_URL) ||
    isSubUrlOf(window.location.pathname, LEGACY_BUILDER_BASE_URL)
  );
};

const isVBOrLegacyUrlFromState = (state: Object) =>
  isSubUrlOf(RouterSelectors.getCurrentPathname(state), BASE_APP_URL) ||
  isSubUrlOf(
    RouterSelectors.getCurrentPathname(state),
    LEGACY_BUILDER_BASE_URL
  );

export {
  getActionUrl,
  getAdvancedUrl,
  getAppUrl,
  getAppVersionUrl,
  getAuthUrlForFieldKey,
  getBaseAuthUrl,
  getServiceUrl,
  getTriggerUrl,
  isVBOrLegacyUrl,
  isVBOrLegacyUrlFromState,
};
